'use client';

import { useState, useEffect, ChangeEvent } from 'react';
import { NewsletterSignupBlock as INewsletterSignupBlock } from '../types';
import { SupportedLanguages } from '@/types/globals';

const text = {
  fr: {
    successMessage:
      "Vous avez été ajouté avec succès à notre newsletter. Nous vous tiendrons informé des dernières tendances et perspectives de l'industrie.",
    thankYou: 'Merci!',
    errorMessage: 'Une erreur est survenue. Veuillez réessayer.',
    firstNamePlaceholder: 'Prénom',
    lastNamePlaceholder: 'Nom',
    emailPlaceholder: 'Email',
    sending: "En cours d'envoi...",
    signUp: "S'inscrire",
  },
  en: {
    successMessage:
      "You've been successfully added to our newsletter. We'll keep you informed on the latest industry trends and insights.",
    thankYou: 'Thank You!',
    errorMessage: 'Something went wrong. Please try again.',
    firstNamePlaceholder: 'First name',
    lastNamePlaceholder: 'Last name',
    emailPlaceholder: 'Email',
    sending: 'Signing Up...',
    signUp: 'Sign Up',
  },
};

export default function NewsletterSignupBlock({
  block,
  locale,
}: {
  block: INewsletterSignupBlock;
  locale: SupportedLanguages;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (element: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = element.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const submitToHubSpot = async (formData: {
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    const hubspotPortalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
    const hubspotFormId = process.env.NEXT_PUBLIC_NEWSLETTER_FORM_ID;

    if (!hubspotPortalId || !hubspotFormId) {
      throw new Error('HubSpot configuration missing');
    }

    const payload = {
      fields: [
        {
          name: 'firstname',
          value: formData.firstName,
        },
        {
          name: 'lastname',
          value: formData.lastName,
        },
        {
          name: 'email',
          value: formData.email,
        },
      ],
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotPortalId}/${hubspotFormId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Form submission failed: ${response.status} - ${errorText}`,
      );
    }

    return response.json();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await submitToHubSpot(formData);
      setIsSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(text[locale].errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!mounted) {
    // This was added because plugins like nordpass are altering the dom and next throws hydration errors
    return <div style={{ minHeight: 160 }} />;
  }

  if (isSubmitted) {
    return (
      <section className="relative overflow-hidden px-4 py-20">
        <div className="relative mx-auto max-w-6xl text-center">
          <div className="mx-auto max-w-2xl">
            <h2
              className="mb-6 text-3xl font-bold md:text-4xl"
              dangerouslySetInnerHTML={{ __html: text[locale].thankYou }}
            />
            <p
              className="text-lg opacity-90 md:text-xl"
              dangerouslySetInnerHTML={{ __html: text[locale].successMessage }}
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden px-4 py-20">
      <div className="relative mx-auto max-w-6xl text-center">
        {/* Newsletter label */}
        <div className="mb-8">
          <span className="text-sm font-semibold tracking-wider uppercase opacity-90">
            <span dangerouslySetInnerHTML={{ __html: block.pre_title || '' }} />
          </span>
        </div>

        {/* Main heading */}
        <div className="mx-auto mb-8 max-w-2xl">
          <h2
            className="mb-6 text-3xl font-bold md:text-4xl"
            dangerouslySetInnerHTML={{ __html: block.title || '' }}
          />
          <div
            className="text-lg opacity-90 md:text-xl"
            dangerouslySetInnerHTML={{ __html: block.description || '' }}
          />
        </div>

        {/* Custom Form */}
        <form
          onSubmit={handleSubmit}
          className="mx-auto max-w-4xl"
          suppressHydrationWarning
        >
          <div className="flex flex-col items-end gap-4 md:flex-row">
            {/* First Name Input */}
            <div className="flex-1">
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={text[locale].firstNamePlaceholder}
                required
                className="focus:ring-opacity-50 w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-500 transition-all focus:ring-2 focus:ring-white focus:outline-none"
              />
            </div>

            {/* Last Name Input */}
            <div className="flex-1">
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder={text[locale].lastNamePlaceholder}
                required
                className="focus:ring-opacity-50 w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-500 transition-all focus:ring-2 focus:ring-white focus:outline-none"
              />
            </div>

            {/* Email Input */}
            <div className="flex-1">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={text[locale].emailPlaceholder}
                required
                className="focus:ring-opacity-50 w-full rounded-lg bg-white px-6 py-4 text-gray-900 placeholder-gray-500 transition-all focus:ring-2 focus:ring-white focus:outline-none"
              />
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="hover:bg-opacity-90 focus:ring-opacity-50 flex items-center gap-2 rounded bg-white px-8 py-4 font-semibold text-teal-600 transition-all focus:ring-2 focus:ring-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-teal-600 border-t-transparent"></div>
                    {text[locale].sending}
                  </>
                ) : (
                  <>{text[locale].signUp}</>
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-opacity-20 mt-4 rounded-lg border border-red-300 bg-red-500 p-3 text-sm text-red-100">
              {error}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
